import { useEffect, useState, Fragment } from 'react';
import { useRouter } from 'next/router';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useObjectsProvider } from '../../context/objects';
import { useLocationState } from '../../context/location';
import { useIsMount } from '../../hooks/useIsMount';
import { categoryCaseConverter, declOfNum, JSONToQuery, sleep } from '../../helpers';
import Skelet from '../Skelet';
import ObjectsCatalogItem from './ObjectsCatalogItem';
import ObjectsCatalogSort from './ObjectsCatalogSort';
import ObjectsCatalogSkeletonItem from './ObjectsCatalogSkeletonItem';
import ObjectsCatalogCommercialItem from './ObjectsCatalogCommercialItem';
import styles from '../../styles/modules/objects/ObjectsCatalog.module.scss';

const generatePagination = (current, last) => {
  const delta = 1;
  const left = current - delta;
  const right = current + delta + 1;
  const range = [];
  const rangeWithDots = [];

  let l;

  for (let i = 1; i <= last; i += 1) {
    if (i === 1 || i === last || (i >= left && i < right)) {
      range.push(i);
    }
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push(null);
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
};

const ObjectsCatalog = () => {
  const router = useRouter();
  const isMount = useIsMount();
  const locationState = useLocationState();
  const [objectsState, setObjectsState] = useObjectsProvider();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState(null);
  const [total, setTotal] = useState(null);
  const [page, setPage] = useState(null);
  const [pages, setPages] = useState(null);

  const handlePagingClick = (value, modificator = 0) => {
    if (value + modificator === page || value + modificator <= 0 || value + modificator > pages)
      return;

    setPage(value + modificator);
    setObjectsState({ type: 'SET_PAGINATION', payload: { page: value + modificator } });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const isSortSelected = (sort, dir) => {
    if (!sort) return false;

    if (objectsState.sorting.sort === sort) {
      if (dir) {
        if (objectsState.sorting.sortDirection === dir) return true;

        return false;
      }

      return true;
    }

    return false;
  };
  const handleMoveToMap = () => {
    router.push(
      `/objects/map${JSONToQuery(
        { ...objectsState.filter, ...objectsState.pagination, ...objectsState.sorting },
        [
          'length',
          objectsState.filter.studio && !objectsState.filter.rooms.length && 'studio',
          !objectsState.filter.studio && objectsState.filter.rooms.length === 1 && 'rooms'
        ],
        ['page=1', 'sort=last-update.date']
      )}`,
      undefined,
      {
        scroll: false
      }
    );
  };

  useEffect(() => {
    if (!locationState.isLoaded) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        await sleep(300);

        const response = await axios.post(
          process.env.NEXT_PUBLIC_API + '/objects',
          {
            ...objectsState.filter,
            ...objectsState.sorting,
            ...objectsState.pagination,
            location: locationState.name
          },
          { signal }
        );

        if (!response.data || response.data.status !== 'ok') throw new Error(response.data.text);

        setTotal(response.data.data.total);
        setPage(response.data.data.current_page);
        setPages(response.data.data.last_page);
        setData(response.data.data.data);
        setIsLoading(false);
        setIsError(false);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          setIsLoading(false);
          setIsError(true);
        }
      }
    };
    fetchData();

    return () => controller.abort();
  }, [objectsState.filter, locationState]);
  useEffect(() => {
    if (!locationState.isLoaded || isMount || isLoading) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setIsLoadingData(true);
        await sleep(300);

        const response = await axios.post(
          process.env.NEXT_PUBLIC_API + '/objects',
          {
            ...objectsState.filter,
            ...objectsState.sorting,
            ...objectsState.pagination,
            location: locationState.name
          },
          { signal }
        );

        if (!response.data || response.data.status !== 'ok') throw new Error(response.data.text);

        setPage(response.data.data.current_page);
        setData(response.data.data.data);
        setIsLoadingData(false);
        setIsError(false);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          setIsLoadingData(false);
          setIsError(true);
        }
      }
    };
    fetchData();

    return () => controller.abort();
  }, [objectsState.pagination]);
  useEffect(() => window.scrollTo({ top: 0, behavior: 'smooth' }), [locationState]);

  if (isLoading || isError) {
    return (
      <section className={['block', styles.clear].join(' ')}>
        <div className={['fixed', styles.catalog].join(' ')}>
          <div className={styles.catalogHeader}>
            <Skelet height={32} maxWidth={400} className={styles.catalogHeaderTitle} />
            <Skelet height={32} maxWidth={200} className={styles.catalogHeaderActions} />
          </div>
          <div className={styles.catalogContent}>
            {[...Array(3).keys()].map((value) => (
              <ObjectsCatalogSkeletonItem key={value} />
            ))}
          </div>
          <Skelet
            height={48}
            maxWidth={300}
            className={styles.catalogFooter}
            style={{ margin: 'auto' }}
          />
        </div>
      </section>
    );
  }

  return (
    <section className={['block', styles.clear].join(' ')}>
      <div className={['fixed', styles.catalog].join(' ')}>
        {!!data.length && (
          <div className={styles.catalogHeader}>
            <div className={styles.catalogHeaderTitle}>
              {declOfNum(total, [
                objectsState.filter.category === 'дом' || objectsState.filter.category === 'участок'
                  ? 'Найден'
                  : 'Найдена',
                'Найдено',
                'Найдено'
              ])}{' '}
              {total} {categoryCaseConverter(objectsState.filter.category, total)}
              <span> в {locationState.case}</span>
            </div>
            <div className={styles.catalogHeaderActions}>
              <button type="button" className={styles.catalogMapButton} onClick={handleMoveToMap}>
                <div style={{ marginRight: 8 }}>На карте</div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="21"
                  height="21"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
                  <line x1="8" y1="2" x2="8" y2="18" />
                  <line x1="16" y1="6" x2="16" y2="22" />
                </svg>
              </button>
              <ObjectsCatalogSort
                content={[
                  {
                    text: 'По актуальности',
                    sort: 'last-update.date',
                    selected: isSortSelected('last-update.date')
                  },
                  {
                    text: 'Цена по возрастанию',
                    sort: 'price.value',
                    direction: 'asc',
                    selected: isSortSelected('price.value', 'asc')
                  },
                  {
                    text: 'Цена по убыванию',
                    sort: 'price.value',
                    direction: 'desc',
                    selected: isSortSelected('price.value', 'desc')
                  }
                ]}
              />
            </div>
          </div>
        )}
        <div className={styles.catalogContent}>
          {isLoadingData ? (
            [...Array(3).keys()].map((value) => <ObjectsCatalogSkeletonItem key={value} />)
          ) : !!data.length ? (
            data.map((value, index) => {
              if (data.length > 4 && index === 3) {
                return (
                  <Fragment key={index}>
                    <ObjectsCatalogItem item={value} />
                    <ObjectsCatalogCommercialItem />
                  </Fragment>
                );
              }

              return <ObjectsCatalogItem item={value} key={index} />;
            })
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{
                opacity: 1,
                scale: 1,
                transition: { duration: 1, type: 'spring', bounce: 0.6 }
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <h5 style={{ marginBottom: 8 }}>Нет вариантов в {locationState.case}</h5>
              <div style={{ opacity: 0.6, textAlign: 'center', margin: '0 18px' }}>
                Попробуйте изменить фильтры или расширить область поиска
              </div>
              <img
                src="/images/wondering-man.svg"
                alt=""
                style={{ width: '100%', maxWidth: 400, margin: '18px 32px' }}
              />
            </motion.div>
          )}
        </div>
        {pages > 1 && (
          <div className={styles.catalogFooter}>
            <button
              className={styles.catalogFooterLeft}
              type="button"
              aria-label="Предыдущая страница"
              onClick={() => handlePagingClick(page, -1)}
              style={{
                cursor: page <= 1 ? 'not-allowed' : null,
                opacity: page <= 1 ? 0.1 : null
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            {generatePagination(page, pages).map((value, index) => {
              if (!value) return <span key={index}>...</span>;

              return (
                <button
                  className={[styles.catalogFooterButton, value === page ? styles.active : ''].join(
                    ' '
                  )}
                  type="button"
                  key={index}
                  onClick={() => handlePagingClick(value)}
                  style={{ cursor: value === page ? 'default' : null }}
                >
                  {value}
                </button>
              );
            })}
            <button
              className={styles.catalogFooterRight}
              type="button"
              aria-label="Следующая страница"
              onClick={() => handlePagingClick(page, 1)}
              style={{
                cursor: page >= pages ? 'not-allowed' : null,
                opacity: page >= pages ? 0.1 : null
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ObjectsCatalog;
